import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import StudentLayout from '@/components/layouts/StudentLayout';
import { 
  Brain, ArrowLeft, Trophy, Clock, Sparkles, 
  RotateCcw, Star, Zap
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const MemoryMatchGame = () => {
  const navigate = useNavigate();
  const { api } = useAuth();
  const [searchParams] = useSearchParams();
  const topicId = searchParams.get('topic') || 'ks2-maths-general';
  const difficulty = searchParams.get('difficulty') || 'easy';
  
  const [gameId, setGameId] = useState(null);
  const [cards, setCards] = useState([]);
  const [flippedCards, setFlippedCards] = useState([]);
  const [matchedPairs, setMatchedPairs] = useState([]);
  const [moves, setMoves] = useState(0);
  const [totalPairs, setTotalPairs] = useState(0);
  const [gameComplete, setGameComplete] = useState(false);
  const [pointsEarned, setPointsEarned] = useState(0);
  const [timer, setTimer] = useState(0);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);

  const startGame = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.post('/games/memory-match/start', {
        topic_id: topicId,
        difficulty: difficulty
      });
      setGameId(response.data.game_id);
      setCards(response.data.cards.map(c => ({ ...c, flipped: false, matched: false })));
      setTotalPairs(response.data.total_pairs);
      setFlippedCards([]);
      setMatchedPairs([]);
      setMoves(0);
      setTimer(0);
      setGameComplete(false);
      setPointsEarned(0);
    } catch (error) {
      console.error('Error starting game:', error);
      toast.error('Failed to start game');
    } finally {
      setLoading(false);
    }
  }, [api, topicId, difficulty]);

  useEffect(() => {
    startGame();
  }, [startGame]);

  // Timer
  useEffect(() => {
    if (!loading && !gameComplete && gameId) {
      const interval = setInterval(() => {
        setTimer(t => t + 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [loading, gameComplete, gameId]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleCardClick = async (cardIndex) => {
    if (checking || flippedCards.length >= 2) return;
    if (cards[cardIndex].flipped || cards[cardIndex].matched) return;

    try {
      // Reveal card
      const response = await api.post(`/games/memory-match/${gameId}/reveal?card_id=${cards[cardIndex].id}`);
      
      const newCards = [...cards];
      newCards[cardIndex] = {
        ...newCards[cardIndex],
        flipped: true,
        content: response.data.content,
        pair_id: response.data.pair_id
      };
      setCards(newCards);
      
      const newFlipped = [...flippedCards, cardIndex];
      setFlippedCards(newFlipped);

      // Check for match if two cards are flipped
      if (newFlipped.length === 2) {
        setChecking(true);
        setMoves(m => m + 1);
        
        const matchResponse = await api.post(
          `/games/memory-match/${gameId}/match?card1_id=${newCards[newFlipped[0]].id}&card2_id=${newCards[newFlipped[1]].id}`
        );

        if (matchResponse.data.is_match) {
          // Mark as matched
          const matchedCards = [...cards];
          matchedCards[newFlipped[0]].matched = true;
          matchedCards[newFlipped[1]].matched = true;
          setCards(matchedCards);
          setMatchedPairs([...matchedPairs, newCards[newFlipped[0]].pair_id]);
          
          if (matchResponse.data.game_complete) {
            setGameComplete(true);
            setPointsEarned(matchResponse.data.points_earned);
            toast.success(`🎉 You earned ${matchResponse.data.points_earned} points!`);
          }
        } else {
          // Flip cards back after delay
          setTimeout(() => {
            const resetCards = [...newCards];
            resetCards[newFlipped[0]].flipped = false;
            resetCards[newFlipped[1]].flipped = false;
            setCards(resetCards);
          }, 1000);
        }
        
        setTimeout(() => {
          setFlippedCards([]);
          setChecking(false);
        }, 1000);
      }
    } catch (error) {
      console.error('Error handling card:', error);
    }
  };

  const gridCols = difficulty === 'easy' ? 4 : difficulty === 'medium' ? 4 : 4;

  return (
    <StudentLayout>
      <div data-testid="memory-match-game" className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button 
            onClick={() => navigate('/student/games')}
            className="flex items-center gap-2 text-slate-600 hover:text-purple-600 font-medium"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Games
          </button>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 bg-purple-100 px-4 py-2 rounded-full">
              <Clock className="w-5 h-5 text-purple-600" />
              <span className="font-bold text-purple-700">{formatTime(timer)}</span>
            </div>
            <div className="flex items-center gap-2 bg-amber-100 px-4 py-2 rounded-full">
              <Zap className="w-5 h-5 text-amber-600" />
              <span className="font-bold text-amber-700">{moves} moves</span>
            </div>
          </div>
        </div>

        {/* Game Header */}
        <div className="student-card bg-purple-100 border-purple-300 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-purple-500 rounded-2xl border-2 border-gray-800 flex items-center justify-center">
                <Brain className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="font-heading text-2xl font-bold text-slate-800">Memory Match</h1>
                <p className="text-slate-600">Match the pairs! Found: {matchedPairs.length} / {totalPairs}</p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={startGame}
              className="border-purple-400 text-purple-700"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Restart
            </Button>
          </div>
        </div>

        {/* Game Board */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent"></div>
          </div>
        ) : gameComplete ? (
          <div className="student-card text-center py-12">
            <div className="w-20 h-20 bg-amber-400 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
              <Trophy className="w-12 h-12 text-white" />
            </div>
            <h2 className="font-heading text-3xl font-bold text-slate-800 mb-2">
              Congratulations! 🎉
            </h2>
            <p className="text-xl text-slate-600 mb-6">
              You completed the game in {moves} moves and {formatTime(timer)}!
            </p>
            <div className="flex items-center justify-center gap-2 mb-8">
              <Star className="w-8 h-8 text-amber-400 fill-amber-400" />
              <span className="font-heading text-4xl font-bold text-amber-600">+{pointsEarned}</span>
              <span className="text-slate-600 text-xl">points</span>
            </div>
            <div className="flex gap-4 justify-center">
              <Button
                variant="outline"
                onClick={startGame}
                className="border-purple-400 text-purple-700"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Play Again
              </Button>
              <Button
                onClick={() => navigate('/student/games')}
                className="student-btn"
              >
                More Games
              </Button>
            </div>
          </div>
        ) : (
          <div 
            className={`grid gap-4`}
            style={{ gridTemplateColumns: `repeat(${gridCols}, 1fr)` }}
          >
            {cards.map((card, index) => (
              <button
                key={card.id}
                onClick={() => handleCardClick(index)}
                disabled={card.flipped || card.matched || checking}
                className={`
                  aspect-square rounded-2xl border-3 transition-all duration-300 transform
                  ${card.matched 
                    ? 'bg-emerald-100 border-emerald-400 scale-95' 
                    : card.flipped 
                      ? 'bg-white border-purple-400 rotate-y-180' 
                      : 'bg-purple-500 border-purple-700 hover:scale-105 hover:bg-purple-400 cursor-pointer'
                  }
                `}
                data-testid={`card-${index}`}
              >
                {(card.flipped || card.matched) ? (
                  <div className="p-3 h-full flex items-center justify-center">
                    <span className={`text-center font-semibold ${card.matched ? 'text-emerald-700' : 'text-slate-800'}`}>
                      {card.content}
                    </span>
                  </div>
                ) : (
                  <div className="h-full flex items-center justify-center">
                    <Sparkles className="w-8 h-8 text-purple-200" />
                  </div>
                )}
              </button>
            ))}
          </div>
        )}

        {/* Tips */}
        {!gameComplete && !loading && (
          <div className="mt-6 text-center text-slate-500">
            <p>Click cards to flip them. Match terms with their definitions!</p>
          </div>
        )}
      </div>
    </StudentLayout>
  );
};

export default MemoryMatchGame;
